import { Archive, ArrowLeft, Cpu, Mail, MailX, RotateCcw, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client';
import type {
  AlertLine,
  AlertRoute,
  AlertingPayload,
  NodeAlertLines,
  OutboxAlert,
  OutboxPayload,
  SettingSource,
} from '../api/types';
import { ScreenLink } from '../components/ScreenLink';
import { ago } from '../lib/format';
import { can, useMe } from '../lib/me';
import { useLoad } from '../lib/useLoad';

/**
 * How sphere-watch emails people about incidents: the SendGrid key and sender,
 * and who gets which incidents.
 *
 * Every setting says where its value comes from — saved here, or the server's
 * environment — because a setting whose source you cannot see is the one you
 * change in the wrong place. Saved here wins; "Use the environment's" hands it
 * back. The key is write-only: what comes back is its last four characters.
 *
 * Under Systems rather than a tab of its own: it is about sphere-watch itself,
 * like its log.
 *
 * Two tiers of recipient. Most are told when an incident OPENS and when it
 * resolves. One marked "escalation only" is told nothing at open: only when a
 * critical is still unacknowledged after the escalation window, and then when
 * that incident resolves — the person brought in because the first tier has
 * not answered.
 *
 * And what did NOT arrive. A notification is retried for a day and then gives
 * up, and until this screen listed them a row that gave up was read by nobody:
 * the settings above could all look right while every email of the night had
 * failed. Each is listed with what SendGrid or the hook last said, and Retry
 * sends one again from the start of its schedule — once whatever was wrong
 * (usually the key or the sender) is put right. Dismiss sets one aside as
 * seen and not to be sent, which is the only way out of the give-up count for
 * a row Retry refuses.
 *
 * And the lines the app boxes alert at: CPU and memory on dispatch's active
 * and backup boxes (infra.node.resources). Set here, they apply on the next
 * reading with no restart; cleared, the environment's or the default applies.
 */

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical only',
  high: 'High and critical',
  warning: 'Warning and above',
  info: 'Everything',
};

type Field = 'apiKey' | 'from' | 'fromName';

type When = 'opens' | 'escalates';

/** "15 min", from the server's WATCH_ESCALATE_AFTER_MS rather than a number that is true until it changes. */
const windowLabel = (ms: number) => (ms % 3_600_000 === 0 ? `${ms / 3_600_000} h` : `${Math.round(ms / 60_000)} min`);

const whenLabel = (when: When, escalateAfterMs: number) =>
  when === 'escalates' ? `Escalations only (${windowLabel(escalateAfterMs)})` : 'When it opens';

const FIELDS: {
  field: Field;
  label: string;
  secret?: boolean;
  placeholder: string;
  help: string;
  /** What applies when nothing is set anywhere, if anything does. */
  fallback?: string;
}[] = [
  {
    field: 'apiKey',
    label: 'API key',
    secret: true,
    placeholder: 'SG.…',
    help: 'Needs the “Mail Send” permission. Checked with SendGrid before it is saved, and never shown again.',
  },
  {
    field: 'from',
    label: 'Sender address',
    placeholder: 'alerts@limosphere.com',
    help: 'Must be a verified sender on the SendGrid account, or every email is refused.',
  },
  {
    field: 'fromName',
    label: 'Sender name',
    placeholder: 'sphere-watch',
    help: 'What the inbox shows as who it is from.',
    fallback: 'sphere-watch',
  },
];

function Source({ setting, secret, fallback }: { setting: SettingSource; secret?: boolean; fallback?: string }) {
  const shown = secret ? setting.hint : setting.value;
  if (setting.source === 'console') {
    return (
      <>
        <span className="wt-mono">{shown}</span>
        <span className="wt-setting-src">
          saved here by {setting.updatedBy} {setting.updatedAt ? ago(setting.updatedAt) : ''}
          {setting.environment ? ` · overrides ${setting.environment.env}` : ''}
        </span>
      </>
    );
  }
  if (setting.source === 'environment') {
    return (
      <>
        <span className="wt-mono">{shown}</span>
        <span className="wt-setting-src">from the server’s environment ({setting.environment?.env})</span>
      </>
    );
  }
  if (fallback) {
    return (
      <>
        <span className="wt-mono">{fallback}</span>
        <span className="wt-setting-src">the default</span>
      </>
    );
  }
  return <span className="wt-setting-src wt-setting-none">not set</span>;
}

function SettingRow({
  spec,
  setting,
  canEdit,
  onSave,
}: {
  spec: (typeof FIELDS)[number];
  setting: SettingSource;
  canEdit: boolean;
  onSave: (field: Field, value: string | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (value: string | null) => {
    setBusy(true);
    const ok = await onSave(spec.field, value);
    setBusy(false);
    if (ok) {
      setEditing(false);
      setDraft('');
    }
  };

  return (
    <div className="wt-setting">
      <div className="wt-setting-label">{spec.label}</div>
      <div className="wt-setting-body">
        {editing ? (
          <form
            className="wt-setting-form"
            onSubmit={(event) => {
              event.preventDefault();
              void save(draft.trim());
            }}
          >
            <input
              className="input"
              type={spec.secret ? 'password' : 'text'}
              autoComplete="off"
              placeholder={spec.placeholder}
              value={draft}
              // biome-ignore lint/a11y/noAutofocus: the field appears because "Change" was just pressed; focus belongs in it
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
            />
            <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim()}>
              {busy ? (spec.secret ? 'Checking…' : 'Saving…') : 'Save'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="wt-setting-value">
            <Source setting={setting} secret={spec.secret} fallback={spec.fallback} />
          </div>
        )}
        <div className="field-hint">{spec.help}</div>
      </div>
      {canEdit && !editing && (
        <div className="wt-setting-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setDraft(spec.secret ? '' : (setting.value ?? ''));
              setEditing(true);
            }}
          >
            {setting.source === 'none' ? 'Set' : spec.secret ? 'Replace' : 'Change'}
          </button>
          {setting.source === 'console' && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              title={
                setting.environment
                  ? `Remove the value saved here; ${setting.environment.env} applies again`
                  : 'Remove the value saved here; nothing is set in the environment'
              }
              onClick={() => void save(null)}
            >
              {setting.environment ? 'Use the environment’s' : 'Remove'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function RouteRow({
  route,
  severities,
  escalateAfterMs,
  canEdit,
  onChange,
  onRemove,
  onTest,
}: {
  route: AlertRoute;
  severities: string[];
  escalateAfterMs: number;
  canEdit: boolean;
  onChange: (route: AlertRoute, changes: Partial<AlertRoute>) => void;
  onRemove: (route: AlertRoute) => void;
  onTest: (to: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <tr style={route.enabled ? undefined : { opacity: 0.6 }}>
      <td className="wt-stack-wide">
        <div className="wt-mono">{route.address}</div>
        {route.system && <div className="wt-setting-src">only {route.system}</div>}
      </td>
      <td className="wt-stack-wide" data-label="Gets">
        {route.escalationOnly ? (
          // Only criticals escalate, whatever the floor says; a select here
          // would offer a choice that changes nothing.
          <span title="Only critical incidents escalate">Critical only</span>
        ) : canEdit ? (
          <select
            className="select wt-route-select"
            value={route.severityMin}
            onChange={(event) => onChange(route, { severityMin: event.target.value })}
          >
            {severities.map((severity) => (
              <option key={severity} value={severity}>
                {SEVERITY_LABEL[severity] ?? severity}
              </option>
            ))}
          </select>
        ) : (
          (SEVERITY_LABEL[route.severityMin] ?? route.severityMin)
        )}
      </td>
      <td className="wt-stack-wide" data-label="When">
        {canEdit ? (
          <select
            className="select wt-route-select"
            aria-label={`When ${route.address} is told`}
            value={route.escalationOnly ? 'escalates' : 'opens'}
            onChange={(event) => onChange(route, { escalationOnly: event.target.value === 'escalates' })}
          >
            {(['opens', 'escalates'] as When[]).map((when) => (
              <option key={when} value={when}>
                {whenLabel(when, escalateAfterMs)}
              </option>
            ))}
          </select>
        ) : (
          whenLabel(route.escalationOnly ? 'escalates' : 'opens', escalateAfterMs)
        )}
      </td>
      <td data-label="On">
        <label className="toggle" title={route.enabled ? 'Emailed' : 'Not emailed'}>
          <input
            type="checkbox"
            checked={route.enabled}
            disabled={!canEdit}
            onChange={(event) => onChange(route, { enabled: event.target.checked })}
          />
          <span className="toggle-track" />
        </label>
      </td>
      {canEdit && (
        <td className="wt-route-actions">
          <button type="button" className="btn btn-ghost" title={`Send a test email to ${route.address}`} onClick={() => onTest(route.address)}>
            <Send size={13} /> Test
          </button>
          {confirming ? (
            <button type="button" className="btn btn-danger" onClick={() => onRemove(route)}>
              Remove?
            </button>
          ) : (
            <button type="button" className="btn btn-ghost btn-icon" title="Remove" aria-label="Remove" onClick={() => setConfirming(true)}>
              <Trash2 size={14} />
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

type LineField = 'cpuAlertPct' | 'memAlertPct';

const LINES: { field: LineField; label: string }[] = [
  { field: 'cpuAlertPct', label: 'CPU over' },
  { field: 'memAlertPct', label: 'Memory over' },
];

/** A whole percentage from 1 to 100, as the server admits; anything else is not saved. */
const wholePct = (draft: string) => {
  const value = Number(draft.trim());
  return draft.trim() !== '' && Number.isInteger(value) && value >= 1 && value <= 100 ? value : null;
};

function LineRow({
  label,
  line,
  canEdit,
  onSave,
}: {
  label: string;
  line: AlertLine;
  canEdit: boolean;
  onSave: (value: number | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const inForce = line.source === 'none' ? line.default : String(line.value ?? '');
  const parsed = wholePct(draft);

  const save = async (value: number | null) => {
    setBusy(true);
    const ok = await onSave(value);
    setBusy(false);
    if (ok) setEditing(false);
  };

  return (
    <div className="wt-setting">
      <div className="wt-setting-label">{label}</div>
      <div className="wt-setting-body">
        {editing ? (
          <form
            className="wt-setting-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (parsed !== null) void save(parsed);
            }}
          >
            <input
              className="input wt-line-input"
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              step={1}
              aria-label={`${label}, percent`}
              value={draft}
              // biome-ignore lint/a11y/noAutofocus: the field appears because "Change" was just pressed; focus belongs in it
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
            />
            <span className="wt-line-unit">%</span>
            <button type="submit" className="btn btn-primary" disabled={busy || parsed === null}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="wt-setting-value">
            <span className="wt-mono">{inForce}%</span>
            <span className="wt-setting-src">
              {line.source === 'console'
                ? `saved here by ${line.updatedBy} ${line.updatedAt ? ago(line.updatedAt) : ''}${
                    line.environment ? ` · overrides ${line.environment.env}` : ''
                  }`
                : line.source === 'environment'
                  ? `from the server’s environment (${line.environment?.env})`
                  : 'the default'}
            </span>
          </div>
        )}
        {editing && draft.trim() !== '' && parsed === null && (
          <div className="field-hint">A whole number from 1 to 100.</div>
        )}
      </div>
      {canEdit && !editing && (
        <div className="wt-setting-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setDraft(inForce);
              setEditing(true);
            }}
          >
            Change
          </button>
          {line.source === 'console' && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              title={
                line.environment
                  ? `Remove the line saved here; ${line.environment.env} applies again`
                  : `Remove the line saved here; the default (${line.default}%) applies again`
              }
              onClick={() => void save(null)}
            >
              {line.environment ? 'Use the environment’s' : 'Use the default'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The app boxes' alert lines. Says plainly when no box is being read, because
 * lines with nothing behind them look exactly like lines that are working.
 */
function NodeLines({
  nodes,
  canEdit,
  act,
}: {
  nodes: NodeAlertLines;
  canEdit: boolean;
  act: (work: () => Promise<unknown>, success?: string) => Promise<boolean>;
}) {
  return (
    <section className="card card-pad wt-alerting-card">
      <h2 className="wt-alerting-title">
        <Cpu size={16} /> App box CPU and memory
      </h2>
      {nodes.boxes.length === 0 ? (
        <div className="wt-banner wt-banner-warn">
          No app box is being read{nodes.broken ? `: ${nodes.broken} entry in WATCH_NODE_EXPORTERS does not parse` : ': WATCH_NODE_EXPORTERS is not set on the server'}, so these
          lines alert on nothing yet.
        </div>
      ) : (
        <p className="wt-setting-src">
          Watching {nodes.boxes.join(', ')}
          {nodes.broken ? ` — and ${nodes.broken} entry in WATCH_NODE_EXPORTERS that does not parse` : ''}.
        </p>
      )}
      {LINES.map(({ field, label }) => (
        <LineRow
          key={field}
          label={label}
          line={nodes[field]}
          canEdit={canEdit}
          onSave={(value) =>
            act(
              () => api.post('/alerting/nodes', { [field]: value }),
              value === null ? 'Removed. The line from the environment, or the default, applies again.' : `Saved. The next reading is judged at ${value}%.`,
            )
          }
        />
      ))}
      <p className="field-hint">
        An incident opens when a box stays at or over a line for three readings in a row — about three minutes — and
        resolves after two readings under it, so a deploy or a nightly job does not page anyone. CPU is the average
        over the minute between readings; memory is what the kernel cannot hand out without swapping. Both are drawn
        per box on Vitals.
      </p>
    </section>
  );
}

const REASON_LABEL: Record<string, string> = {
  opened: 'opened',
  escalated: 'escalation',
  resolved: 'resolved',
};

/** When the next try is: the clock time it is due at, or "any moment" once due. */
const nextTry = (iso: string) =>
  new Date(iso).getTime() > Date.now()
    ? `at ${new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
    : 'any moment';

function UndeliveredRow({
  alert,
  canEdit,
  busy,
  onRetry,
  onDismiss,
}: {
  alert: OutboxAlert;
  canEdit: boolean;
  busy: boolean;
  onRetry: (alert: OutboxAlert) => void;
  onDismiss: (alert: OutboxAlert) => void;
}) {
  const gaveUp = alert.status === 'failed';
  // Asked twice, like removing a recipient: a dismissed notification is never
  // sent, and cannot be retried afterwards.
  const [confirming, setConfirming] = useState(false);
  return (
    <tr>
      <td className="wt-stack-wide">
        <div>{alert.title}</div>
        <div className="wt-setting-src">
          {REASON_LABEL[alert.reason] ?? alert.reason} · {alert.severity}
          {alert.tenantKey ? ` · ${alert.tenantKey}` : ''} · incident {alert.incidentStatus}
        </div>
      </td>
      <td className="wt-stack-wide" data-label="To">
        <div className="wt-mono">{alert.target}</div>
        <div className="wt-setting-src">{alert.channel}</div>
      </td>
      <td className="wt-outbox-state wt-stack-wide" data-label="State">
        {gaveUp ? (
          <span className="wt-chip wt-chip-failed">gave up</span>
        ) : (
          <span className="wt-chip wt-chip-flap">retrying</span>
        )}
        <div className="wt-setting-src">
          {alert.attempts} {alert.attempts === 1 ? 'try' : 'tries'}
          {gaveUp ? `, last ${ago(alert.updatedAt)}` : alert.nextAttemptAt ? `, next ${nextTry(alert.nextAttemptAt)}` : ''}
        </div>
      </td>
      <td className="wt-outbox-error wt-stack-wide" data-label="Last error">
        {alert.lastError ?? '—'}
      </td>
      {canEdit && (
        <td className="wt-outbox-action wt-stack-wide">
          {!alert.canRetry && alert.retryRefusal && <span className="wt-outbox-refusal">{alert.retryRefusal}</span>}
          <div className="wt-route-actions">
            {alert.canRetry && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                title="Send it again, from the start of its schedule"
                onClick={() => onRetry(alert)}
              >
                <RotateCcw size={13} /> Retry
              </button>
            )}
            {alert.canDismiss &&
              (confirming ? (
                <button type="button" className="btn btn-danger" disabled={busy} onClick={() => onDismiss(alert)}>
                  Dismiss?
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  title="Seen, and not to be sent: take it out of the count of notifications that gave up"
                  onClick={() => setConfirming(true)}
                >
                  <Archive size={13} /> Dismiss
                </button>
              ))}
          </div>
        </td>
      )}
    </tr>
  );
}

/**
 * Its own load, polled: the rest of the screen is settings, which change when
 * somebody changes them, and this is the one part that changes on its own.
 */
function Undelivered({ canEdit }: { canEdit: boolean }) {
  const { data, error, reload } = useLoad(() => api.get<OutboxPayload>('/alerting/outbox'), [], 30_000);
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  const retry = async (alert: OutboxAlert) => {
    setBusy(alert.id);
    setNote(null);
    try {
      await api.post(`/alerting/outbox/${alert.id}/requeue`);
      setNote({ tone: 'ok', text: `Queued again: ${alert.reason} of “${alert.title}” to ${alert.target}.` });
    } catch (caught) {
      setNote({ tone: 'danger', text: caught instanceof Error ? caught.message : String(caught) });
    }
    setBusy(null);
    void reload();
  };

  /**
   * The other answer to one that gave up: seen, and not to be sent. Without it
   * a row Retry is refused for -- an opening about an incident that has since
   * resolved -- held the banner above, and the export's give-up count, for a
   * day nobody could shorten.
   */
  const dismiss = async (alert: OutboxAlert) => {
    setBusy(alert.id);
    setNote(null);
    try {
      await api.post(`/alerting/outbox/${alert.id}/dismiss`);
      setNote({ tone: 'ok', text: `Dismissed: ${alert.reason} of “${alert.title}” to ${alert.target}.` });
    } catch (caught) {
      setNote({ tone: 'danger', text: caught instanceof Error ? caught.message : String(caught) });
    }
    setBusy(null);
    void reload();
  };

  const health = data?.health;
  return (
    <section className="card card-pad wt-alerting-card">
      <h2 className="wt-alerting-title">
        <MailX size={16} /> What did not arrive
      </h2>
      {error && <div className="wt-banner wt-banner-danger">{error.message}</div>}
      {health && !health.healthy && <div className="wt-banner wt-banner-danger">{health.reason}</div>}
      {note && <p className={`note note-${note.tone}`}>{note.text}</p>}
      {data && data.alerts.length > 0 ? (
        <div className="wt-routes-wrap">
          <table className="wt-table wt-routes wt-stack">
            <thead>
              <tr>
                <th>Incident</th>
                <th>To</th>
                <th>State</th>
                <th>Last error</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {data.alerts.map((alert) => (
                <UndeliveredRow
                  key={alert.id}
                  alert={alert}
                  canEdit={canEdit}
                  busy={busy === alert.id}
                  onRetry={(target) => void retry(target)}
                  onDismiss={(target) => void dismiss(target)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        data && (
          <p className="wt-setting-src">Nothing has given up in the last week, and nothing is being retried.</p>
        )
      )}
      <p className="field-hint">
        A notification that fails is tried again after 30 seconds, 2, 10 and 30 minutes, an hour, and then every two
        hours for a day before it gives up.
        {health?.pending
          ? ` ${health.pending} owed right now — queued, being retried, or waiting out a silence.`
          : ''}
      </p>
    </section>
  );
}

export function Alerting() {
  const me = useMe();
  const canEdit = can(me, 'alerts.manage');
  const { data, error, loading, reload } = useLoad(() => api.get<AlertingPayload>('/alerting'), []);
  const [note, setNote] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const [address, setAddress] = useState('');
  const [severity, setSeverity] = useState('critical');
  const [when, setWhen] = useState<When>('opens');
  const [testing, setTesting] = useState(false);

  /** Every write, with what went wrong said where it happened. */
  const act = async (work: () => Promise<unknown>, success?: string) => {
    setNote(null);
    try {
      const result = (await work()) as { warnings?: string[] } | undefined;
      if (result?.warnings?.length) setNote({ tone: 'warn', text: result.warnings.join(' ') });
      else if (success) setNote({ tone: 'ok', text: success });
      void reload();
      return true;
    } catch (caught) {
      setNote({ tone: 'danger', text: caught instanceof Error ? caught.message : String(caught) });
      return false;
    }
  };

  const test = async (to?: string) => {
    setTesting(true);
    await act(() => api.post('/alerting/test', to ? { to } : {}), `Test email sent to ${to ?? me?.email}. If it does not arrive, check SendGrid’s activity feed.`);
    setTesting(false);
  };

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (error || !data) return <div className="wt-banner wt-banner-danger">{error?.message ?? 'Could not load alerting.'}</div>;

  const { apiKey, from } = data.email;
  // The first tier: told when an incident opens. An escalation-only recipient
  // hears only what nobody in it answered, so it is not somebody being told.
  const listening = data.routes.filter((route) => route.enabled && !route.escalationOnly).length;
  const escalating = data.routes.filter((route) => route.enabled && route.escalationOnly).length;
  const problem =
    apiKey.source === 'none'
      ? 'Email alerts cannot be sent: there is no SendGrid API key.'
      : from.source === 'none'
        ? 'Email alerts cannot be sent: there is no sender address.'
        : listening === 0 && data.webhookRoutes === 0
          ? escalating > 0
            ? 'Nobody is told when an incident opens: every recipient is escalation only.'
            : 'Nobody will be told about an incident: there are no recipients.'
          : null;

  return (
    <>
      <div className="wt-head">
        <ScreenLink to="/systems" previewTab="systems" className="btn btn-ghost btn-icon" title="Back to Systems">
          <ArrowLeft size={15} />
        </ScreenLink>
        <h1>Alerting</h1>
        <span className="sub">how sphere-watch tells people about incidents, and when the app boxes raise one</span>
      </div>

      {apiKey.unreadable && (
        <div className="wt-banner wt-banner-danger">
          The SendGrid key saved here ({apiKey.unreadable.hint}, by {apiKey.unreadable.updatedBy}) can no longer be read —
          {/* An older server sends no reason; this was its only one. */}{' '}
          {apiKey.unreadable.reason ?? 'the server’s DATABASE_PASSWORD has changed since it was saved'}.{' '}
          {apiKey.source === 'environment'
            ? `The environment’s key (${apiKey.hint}) is being used meanwhile.`
            : 'No email can be sent until it is entered again.'}{' '}
          Enter it again below.
        </div>
      )}
      {problem && <div className="wt-banner wt-banner-warn">{problem}</div>}
      {note && <p className={`note note-${note.tone}`}>{note.text}</p>}

      <section className="card card-pad wt-alerting-card">
        <h2 className="wt-alerting-title">
          <Mail size={16} /> SendGrid
        </h2>
        {FIELDS.map((spec) => (
          <SettingRow
            key={spec.field}
            spec={spec}
            setting={data.email[spec.field]}
            canEdit={canEdit}
            onSave={(field, value) =>
              act(
                () => api.post('/alerting/email', { [field]: value }),
                value === null ? 'Removed. The environment’s value applies again.' : 'Saved. The next alert uses it.',
              )
            }
          />
        ))}
        {canEdit && (
          <div className="wt-alerting-foot">
            <button type="button" className="btn btn-secondary" disabled={testing} onClick={() => void test()}>
              <Send size={14} /> {testing ? 'Sending…' : `Send a test email to ${me?.email}`}
            </button>
          </div>
        )}
      </section>

      <section className="card card-pad wt-alerting-card">
        <h2 className="wt-alerting-title">Who is emailed</h2>
        {data.routes.length > 0 ? (
          // Its own scroll: with the When column a narrow window is wider than
          // the card, and the page itself must not scroll sideways.
          <div className="wt-routes-wrap">
            <table className="wt-table wt-routes wt-stack">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Gets</th>
                  <th>When</th>
                  <th>On</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {data.routes.map((route) => (
                  <RouteRow
                    key={route.id}
                    route={route}
                    severities={data.severities}
                    escalateAfterMs={data.escalateAfterMs}
                    canEdit={canEdit}
                    onChange={(target, changes) => void act(() => api.post(`/alerting/routes/${target.id}`, changes))}
                    onRemove={(target) => void act(() => api.del(`/alerting/routes/${target.id}`), `${target.address} removed.`)}
                    onTest={(to) => void test(to)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="wt-setting-src">Nobody yet.</p>
        )}
        {canEdit && (
          <form
            className="wt-route-add"
            onSubmit={(event) => {
              event.preventDefault();
              const body = {
                address,
                severityMin: when === 'escalates' ? 'critical' : severity,
                escalationOnly: when === 'escalates',
              };
              void act(() => api.post('/alerting/routes', body), `${address.trim()} added.`).then(
                (ok) => ok && setAddress(''),
              );
            }}
          >
            <input
              className="input"
              type="email"
              placeholder="someone@limosphere.com"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
            <select
              className="select wt-route-select"
              value={when === 'escalates' ? 'critical' : severity}
              disabled={when === 'escalates'}
              title={when === 'escalates' ? 'Only critical incidents escalate' : undefined}
              onChange={(event) => setSeverity(event.target.value)}
            >
              {data.severities.map((entry) => (
                <option key={entry} value={entry}>
                  {SEVERITY_LABEL[entry] ?? entry}
                </option>
              ))}
            </select>
            <select
              className="select wt-route-select"
              aria-label="When they are told"
              value={when}
              onChange={(event) => setWhen(event.target.value as When)}
            >
              {(['opens', 'escalates'] as When[]).map((entry) => (
                <option key={entry} value={entry}>
                  {whenLabel(entry, data.escalateAfterMs)}
                </option>
              ))}
            </select>
            <button type="submit" className="btn btn-primary" disabled={!address.trim()}>
              Add
            </button>
          </form>
        )}
        <p className="field-hint">
          “{whenLabel('escalates', data.escalateAfterMs)}” is the second tier: told nothing when an incident opens, only
          when a critical is still unacknowledged {windowLabel(data.escalateAfterMs)} later, and then when it resolves.
          Acknowledging an incident on the board stops it escalating.
        </p>
        {data.webhookRoutes > 0 && (
          <p className="wt-drawer-foot">
            Plus {data.webhookRoutes} webhook route{data.webhookRoutes === 1 ? '' : 's'} (Slack or Telegram), set from the
            server’s environment and not managed here.
          </p>
        )}
      </section>

      {data.nodes && <NodeLines nodes={data.nodes} canEdit={canEdit} act={act} />}

      <Undelivered canEdit={canEdit} />
    </>
  );
}
